// PROPERTY CONSTANTS
export const DEFAULT_MAP_CENTER = {
  lat: 41.033,
  lng: -74.6385,
};

export const PROPERTY_CACHE_KEY = "rentduo_property_cache_v1";
export const SEEDED_PROPERTY_TARGET = 100;

const SEEDED_PROPERTY_CENTER = { lat: 40.7282, lng: -74.0776 };

const SEEDED_PROPERTY_STREETS = [
  "Main St",
  "Broad St",
  "Park Ave",
  "Maple Ave",
  "Washington St",
  "Elm St",
  "Highland Ave",
  "Oak St",
  "Chestnut St",
  "River Rd",
];

const SEEDED_PROPERTY_CITIES = [
  { city: "Newark", state: "NJ", zip: "07102" },
  { city: "Jersey City", state: "NJ", zip: "07302" },
  { city: "Paterson", state: "NJ", zip: "07501" },
  { city: "Elizabeth", state: "NJ", zip: "07201" },
  { city: "Hoboken", state: "NJ", zip: "07030" },
  { city: "Union City", state: "NJ", zip: "07087" },
  { city: "Bayonne", state: "NJ", zip: "07002" },
  { city: "Clifton", state: "NJ", zip: "07011" },
  { city: "Passaic", state: "NJ", zip: "07055" },
  { city: "Bloomfield", state: "NJ", zip: "07003" },
];

const getSeededPropertyOffset = () => (Math.random() - 0.5) * 0.18;

const getSeededPropertyNumber = (min = 10, max = 999) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const buildSeededProperty = ({
  index = 0,
  userId = "",
  userEmail = "",
  userDisplayName = "",
}) => {
  const cityMeta = SEEDED_PROPERTY_CITIES[index % SEEDED_PROPERTY_CITIES.length];
  const streetName = SEEDED_PROPERTY_STREETS[index % SEEDED_PROPERTY_STREETS.length];
  const streetNumber = getSeededPropertyNumber();
  const streetLine = `${streetNumber} ${streetName}`;
  const lat = Number((SEEDED_PROPERTY_CENTER.lat + getSeededPropertyOffset()).toFixed(6));
  const lng = Number((SEEDED_PROPERTY_CENTER.lng + getSeededPropertyOffset()).toFixed(6));

  return {
    ownerId: "",
    ownerEmail: "",
    ownerDisplayName: "",
    ownerPhotoURL: "",
    ownerVerified: false,
    claimStatus: "unverified",
    claimedByUserId: "",
    claimRequestedByUserId: "",
    claimRequestedByEmail: "",
    communityAddedByUserId: userId,
    communityAddedByEmail: userEmail,
    communityAddedByDisplayName: userDisplayName || userEmail || "RentDuo",
    managerUserId: "",
    managementType: "community_added",
    propertyStatus: "active",
    title: streetLine,
    address: streetLine,
    fullAddress: `${streetLine}, ${cityMeta.city}, ${cityMeta.state} ${cityMeta.zip}`,
    description: `Community-added property in ${cityMeta.city}, ${cityMeta.state}. No reviews yet.`,
    city: cityMeta.city,
    state: cityMeta.state,
    zip: cityMeta.zip,
    streetLine,
    lat,
    lng,
    source: "seed_script",
    seedLabel: "community_added_unreviewed",
    reviewCount: 0,
    averageRating: 0,
    isSeeded: true,
  };
};

// ADDRESS NORMALIZATION
export const normalizePropertyAddress = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/\bdr\b/g, "drive")
    .replace(/\brd\b/g, "road")
    .replace(/\bst\b/g, "street")
    .replace(/\bave\b/g, "avenue")
    .replace(/\bblvd\b/g, "boulevard")
    .replace(/\bln\b/g, "lane")
    .replace(/[.#]/g, "")
    .replace(/\b(apartment|apt|unit|suite|ste|floor|fl)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// BLOCK BAD PROPERTIES
export const isBlockedPropertyAddress = (value = "") => {
  const normalized = normalizePropertyAddress(value);
  return normalized.includes("14 fenner drive");
};

// MAP HELPERS
export const isFiniteCoordinate = (value) => Number.isFinite(Number(value));

export const sanitizePropertyForMap = (item = {}) => ({
  ...item,
  normalizedAddress:
    item.normalizedAddress ||
    normalizePropertyAddress(
      item?.fullAddress || item?.address || item?.streetLine || item?.title || ""
    ),
  lat: Number(item.lat),
  lng: Number(item.lng),
});

// DEDUPE PROPERTIES
export const dedupePropertiesByAddress = (items = []) => {
  const bestByAddress = new Map();

  const getPriority = (item = {}) => {
    const claimScore =
      item?.claimStatus === "claimed" || item?.verified === true
        ? 3
        : item?.claimStatus === "pending_claim"
        ? 2
        : 1;
    const reviewScore = Number(item?.reviewCount || item?.reviewsCount || item?.ratingData?.count || 0);
    const updatedMs =
      item?.updatedAt?.seconds * 1000 ||
      item?.createdAt?.seconds * 1000 ||
      Date.parse(item?.updatedAt || item?.createdAt || "") ||
      0;

    return claimScore * 1000000000000 + reviewScore * 1000000 + updatedMs;
  };

  items.forEach((item) => {
    const normalized =
      item?.normalizedAddress ||
      normalizePropertyAddress(
      item?.fullAddress || item?.address || item?.streetLine || item?.title || ""
    );

    if (!normalized || isBlockedPropertyAddress(normalized)) return;

    const enriched = { ...item, normalizedAddress: normalized };
    const existing = bestByAddress.get(normalized);
    if (!existing || getPriority(enriched) > getPriority(existing)) {
      bestByAddress.set(normalized, enriched);
    }
  });

  return Array.from(bestByAddress.values());
};

// CACHE
export const loadCachedProperties = () => {
  try {
    const raw = window.localStorage.getItem(PROPERTY_CACHE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return dedupePropertiesByAddress(
      parsed
        .map((item) => sanitizePropertyForMap(item))
        .filter(
          (item) =>
            item &&
            isFiniteCoordinate(item.lat) &&
            isFiniteCoordinate(item.lng)
        )
    );
  } catch (error) {
    console.error("Property cache load error:", error);
    return [];
  }
};

export const saveCachedProperties = (properties = []) => {
  try {
    const cleaned = dedupePropertiesByAddress(properties);
    window.localStorage.setItem(
      PROPERTY_CACHE_KEY,
      JSON.stringify(cleaned)
    );
  } catch (error) {
    console.error("Property cache save error:", error);
  }
};

// ADDRESS PARSING
export const parseAddressComponents = (components = []) => {
  const findLong = (type) =>
    components.find((c) => c.types?.includes(type))?.long_name || "";

  const findShort = (type) =>
    components.find((c) => c.types?.includes(type))?.short_name || "";

  return {
    streetLine: [findLong("street_number"), findLong("route")]
      .filter(Boolean)
      .join(" ")
      .trim(),
    city:
      findLong("locality") ||
      findLong("postal_town") ||
      findLong("sublocality") ||
      findLong("administrative_area_level_2"),
    state: findShort("administrative_area_level_1"),
    county: findLong("administrative_area_level_2").replace(/\s+County$/i, ""),
    zip: findLong("postal_code"),
  };
};

// FORMATTERS
export const formatCityState = ({ city = "", state = "" }) =>
  [city, state].filter(Boolean).join(", ");

export const formatFullAddress = ({
  streetLine = "",
  city = "",
  state = "",
  zip = "",
}) =>
  [streetLine, [city, state].filter(Boolean).join(", "), zip]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+,/g, ",")
    .trim();
